import { useAuth } from "../auth/authContext.tsx";

/** Browser-true values — never affected by override. Use these to show the
 *  user what their actual browser zone/locale is in the override picker. */
export const getBrowserTimeZone = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone;

export const getBrowserLocale = (): string =>
  Intl.DateTimeFormat().resolvedOptions().locale;

// ── Module-scope accessors ───────────────────────────────────────────────
//
// AuthProvider publishes the live override-aware accessors via the setters
// below. Non-React call sites (cell renderer module helpers, api/endpoints,
// chart utils) read effective tz/locale through getClientTimeZone /
// getClientLocale without prop-drilling. Mirrors the setSessionAccessor
// pattern in api/client.ts.

let tzAccessor: () => string = getBrowserTimeZone;
let localeAccessor: () => string = getBrowserLocale;

export function setClientTimeZoneAccessor(fn: () => string) { tzAccessor = fn; }
export function setClientLocaleAccessor(fn: () => string)   { localeAccessor = fn; }

export function getClientTimeZone(): string { return tzAccessor(); }
export function getClientLocale(): string   { return localeAccessor(); }

// ── React hooks ──────────────────────────────────────────────────────────

export function useClientTimeZone(): string {
  const { clientOverride } = useAuth();
  return clientOverride?.timeZone || getBrowserTimeZone();
}

export function useClientLocale(): string {
  const { clientOverride } = useAuth();
  return clientOverride?.locale || getBrowserLocale();
}

// ── Time-zone math ───────────────────────────────────────────────────────

/** Minutes east of UTC for `date` in `zone` (e.g. Stockholm CEST → +120,
 *  New York EST → -300). Sign convention is *opposite* of
 *  Date.prototype.getTimezoneOffset(); negate when migrating call sites
 *  that previously did `-d.getTimezoneOffset()`. */
export function getTimeZoneOffsetMinutes(zone: string, date: Date): number {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      timeZoneName: "longOffset",
    }).formatToParts(date);
    const tzName = parts.find((p) => p.type === "timeZoneName")?.value ?? "";
    // "GMT+02:00" / "GMT-05:30" / "GMT" (UTC)
    const m = tzName.match(/GMT([+-])(\d{2}):(\d{2})/);
    if (!m) return 0;
    const sign = m[1] === "+" ? 1 : -1;
    return sign * (Number(m[2]) * 60 + Number(m[3]));
  } catch {
    return 0;
  }
}

const zoneNameFormatters = new Map<string, Intl.DateTimeFormat>();

function shortZoneName(zone: string, date: Date, locale: string): string {
  const key = `${locale}|${zone}`;
  let fmt = zoneNameFormatters.get(key);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat(locale, { timeZone: zone, timeZoneName: "short" });
    zoneNameFormatters.set(key, fmt);
  }
  return fmt.formatToParts(date).find((p) => p.type === "timeZoneName")?.value ?? "";
}

const OFFSET_ZONE_NAME_RE = /^(GMT|UTC)[+\-−]/;

/** "CEST" / "EDT" style label for `zone` at `date`. Intl only knows the letter
 *  names in the locales that use them (en-GB has CEST but not EDT, en-US the
 *  reverse), so the viewer's locale is tried first and the two English ones after;
 *  a zone none of them names (Sydney, say) keeps the locale's "GMT+11". */
export function getTimeZoneAbbreviation(zone: string, date: Date, locale: string): string {
  try {
    const own = shortZoneName(zone, date, locale);
    if (!OFFSET_ZONE_NAME_RE.test(own)) return own;
    for (const alt of ["en-GB", "en-US"]) {
      const name = shortZoneName(zone, date, alt);
      if (!OFFSET_ZONE_NAME_RE.test(name)) return name;
    }
    return own;
  } catch {
    return "";
  }
}

/** Wall-clock components for `date` rendered in `zone`. Use when you need
 *  to build a "YYYY-MM-DD HH:mm" string from a Date as-if-in-zone (date
 *  filter offset emission). en-CA gives ISO-like 2-digit values. */
export function getWallClockInZone(zone: string, date: Date): {
  year: number; month: number; day: number; hour: number; minute: number;
} {
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: zone,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    }).formatToParts(date);
    const get = (t: string): number => Number(parts.find((p) => p.type === t)?.value ?? "0");
    let hour = get("hour");
    if (hour === 24) hour = 0; // some impls return "24" for midnight
    return {
      year: get("year"),
      month: get("month"),
      day: get("day"),
      hour,
      minute: get("minute"),
    };
  } catch {
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
      hour: date.getHours(),
      minute: date.getMinutes(),
    };
  }
}

/** True when an active client override changes the perceived zone away
 *  from the browser default. Hot path for opt-in zone math in date
 *  filters — keeps the no-override path identical to legacy behaviour. */
export function hasTimeZoneOverride(): boolean {
  return getClientTimeZone() !== getBrowserTimeZone();
}
